import { useEffect, useState } from 'preact/hooks';
import { useFieldArray } from 'react-hook-form';
import { useLookups } from '@/hooks/useLookups';
import { sanitizeDateTimeInput } from '@/utils/dateTimeFormatters';
import { useAIForm, handleListFieldUpdate } from '@/hooks/useAIForm';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
import { useAgendaEditor } from '@/hooks/useAgendaEditor';
const resolveOptionKey = (val, options) => {
    const v = (val || '').trim();
    if (!v)
        return '';
    const byKey = options.find(o => o.Key === v);
    if (byKey)
        return byKey.Key;
    const byDesc = options.find(o => o.Description === v);
    if (byDesc)
        return byDesc.Key;
    return v;
};
function buildDefaultValues(evt, eventTypeOptions, modeTypeOptions) {
    return {
        overview: evt?.Overview || '',
        agenda: (evt?.Agenda ?? []).map(v => ({ value: v })),
        eventType: resolveOptionKey(evt?.EventType, eventTypeOptions),
        modeType: resolveOptionKey(evt?.ModeType, modeTypeOptions),
        title: evt?.Title || '',
        timeZone: evt?.TimeZone || '',
        startDateTime: sanitizeDateTimeInput(evt?.StartDateTime),
        endDateTime: sanitizeDateTimeInput(evt?.EndDateTime),
        conferenceId: evt?.ConferenceId || '',
        holdCrmSyncUntilEnd: evt?.HoldCrmSyncUntilEnd ?? false,
    };
}
export function useOnlineEventForm({ web, onPatchWeb, speakers, showAddParticipant, showEditModal }) {
    const onlineEvent = web.OnlineEvent;
    const [editingDetails, setEditingDetails] = useState(false);
    const [savingDetails, setSavingDetails] = useState(false);
    const [editingAgenda, setEditingAgenda] = useState(false);
    const [editingOverview, setEditingOverview] = useState(false);
    const [savingOverview, setSavingOverview] = useState(false);
    const anySectionEditing = editingOverview || editingDetails || editingAgenda;
    const modalIsOpen = showAddParticipant || showEditModal;
    const { getOnlineEventTypes, getOnlineModeTypes } = useLookups();
    const onlineEventTypeOptions = getOnlineEventTypes() || [];
    const onlineModeTypeOptions = getOnlineModeTypes() || [];
    const { form } = useAIForm({
        mode: 'onChange',
        defaultValues: buildDefaultValues(onlineEvent, onlineEventTypeOptions, onlineModeTypeOptions),
    }, {
        aiContext: {
            workspaceId: web.WorkspaceId,
            campaignId: web.CampaignId,
            webId: web.Id,
            location: ChatLocations.WebOnlineEventEditor,
            entityType: ChatEntityTypes.Web,
            entityId: web.Id,
            viewMode: anySectionEditing ? ChatViewModes.Edit : ChatViewModes.View,
        },
        fields: [
            { aiName: 'onlineEventOverview', formPath: 'overview', displayName: 'Event Overview', fieldType: 'textarea', maxLength: 5000, isEditable: editingOverview },
            { aiName: 'onlineEventTitle', formPath: 'title', displayName: 'Event Title', fieldType: 'text', maxLength: 200, isEditable: editingDetails },
            { aiName: 'onlineEventAgenda', formPath: 'agenda', displayName: 'Agenda Items', fieldType: 'textarea', isEditable: editingAgenda, isListField: true },
        ],
        loadedData: { speakers: speakers.map(s => ({ name: s.Name, title: s.Title, company: s.CompanyName })) },
        contextSuppressed: modalIsOpen,
        onFieldUpdate: (aiName, value) => {
            if (aiName === 'onlineEventAgenda')
                handleListFieldUpdate(value, append);
        },
    });
    const { register, watch, reset, setValue, getFieldState, formState, control } = form;
    const { fields: agendaFields, append, remove, move } = useFieldArray({ control, name: 'agenda' });
    useEffect(() => {
        reset(buildDefaultValues(onlineEvent, onlineEventTypeOptions, onlineModeTypeOptions));
    }, [onlineEvent]);
    const watched = watch('agenda');
    const { saving, hasDirtyEmpty, normalizedInitial, agendaChanged, saveAgenda, cancelAgendaEdit, } = useAgendaEditor({ web, onPatchWeb, form, watched, getFieldState, formState, setEditingAgenda });
    return {
        editingDetails, setEditingDetails,
        savingDetails, setSavingDetails,
        editingAgenda, setEditingAgenda,
        editingOverview, setEditingOverview,
        savingOverview, setSavingOverview,
        anySectionEditing,
        register, watch, setValue, getFieldState, formState, control,
        agendaFields, append, remove, move,
        watched,
        saving, hasDirtyEmpty, normalizedInitial, agendaChanged,
        saveAgenda, cancelAgendaEdit,
    };
}
